import { z } from "zod";
import { AccountId, LedgerEntryId, SubWalletId, TransactionId, WalletId } from "./ids.ts";
import { MoneyAmount } from "./money.ts";

/**
 * Double-entry direction. A posted transaction writes ≥2 entries that net to
 * zero across the involved wallets. Reversals are *new* entries, never updates.
 */
export const LedgerDirection = z.enum(["debit", "credit"]);
export type LedgerDirection = z.output<typeof LedgerDirection>;

/**
 * Single ledger row. Append-only (enforced by DB trigger in `@ggcoin/db`).
 * `amount` is nonneg — sign is encoded in `direction`.
 *
 * `accountId` is the tenant the entry's wallet belongs to. Most postings are
 * single-account (every entry shares one account), but issuance (a `buy` mints
 * coin: debit the system treasury account, credit the user account) and its
 * reversal span two accounts within one posting. Carrying the account per
 * entry — rather than once per posting — is what lets a single net-to-zero
 * posting move balances across tenants. See `LedgerPosting` (net-zero is
 * global, not per-account, so a mint conserves coin system-wide).
 */
export const LedgerEntry = z.object({
  id: LedgerEntryId,
  accountId: AccountId,
  transactionId: TransactionId,
  walletId: WalletId,
  subWalletId: SubWalletId.nullable(),
  direction: LedgerDirection,
  amount: MoneyAmount,
  postedAt: z.coerce.date(),
});
export type LedgerEntry = z.output<typeof LedgerEntry>;

/**
 * A complete posting for a transaction. Validated as a group so a malformed
 * pair can't slip through individual-entry parsing. Three refines:
 * (1) every entry references the posting's `transactionId`;
 * (2) entry ids are unique within the posting;
 * (3) entries net to zero (debits = credits).
 */
export const LedgerPosting = z
  .object({
    transactionId: TransactionId,
    entries: z.array(LedgerEntry).min(2),
  })
  .refine((p) => p.entries.every((e) => e.transactionId === p.transactionId), {
    message: "every entry must reference the posting's transactionId",
    path: ["entries"],
  })
  .refine((p) => new Set(p.entries.map((e) => e.id)).size === p.entries.length, {
    message: "entry ids must be unique within a posting",
    path: ["entries"],
  })
  .refine(
    (p) => {
      let net = 0n;
      for (const e of p.entries) {
        net += e.direction === "debit" ? -e.amount : e.amount;
      }
      return net === 0n;
    },
    { message: "entries must net to zero (debits = credits)", path: ["entries"] },
  );
export type LedgerPosting = z.output<typeof LedgerPosting>;
